import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Send } from "lucide-react";
import { api } from "../../api";
import { APageHead, ATable, APager, ABadge, AMetric } from "../adminKit";

const STATUS_TONE = { sent: "blue", delivered: "green", queued: "amber", failed: "red", bounced: "red" };
const CATEGORIES = ["", "verification", "password_reset", "welcome", "subscription_confirmation", "billing_receipt",
  "subscription_upgrade", "subscription_downgrade", "subscription_cancellation", "failed_payment",
  "dispute_letter", "report_complete", "funding_report", "contact_notification", "support_ticket_confirmation"];

export default function AdminEmails() {
  const [data, setData] = useState({ items: [], pages: 1, stats: {} });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/emails", { params: { page, status, category, q, limit: 25 } });
      setData(data);
    } catch { toast.error("Could not load emails"); }
    finally { setLoading(false); }
  }, [page, status, category, q]);

  useEffect(() => { load(); }, [load]);

  const resend = async (row) => {
    try {
      await api.post(`/admin/emails/${row.id}/resend`);
      toast.success("Email re-sent");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Resend unavailable for this record"); }
  };

  const s = data.stats || {};
  const columns = [
    { key: "created_at", label: "Sent", render: (r) => new Date(r.created_at).toLocaleString() },
    { key: "category", label: "Type", render: (r) => <span className="capitalize">{(r.category || "").replace(/_/g, " ")}</span> },
    { key: "to", label: "Recipient" },
    { key: "status", label: "Status", render: (r) => <ABadge tone={STATUS_TONE[r.status] || "default"}>{r.status}</ABadge> },
    { key: "attempts", label: "Tries", render: (r) => r.attempts ?? 0 },
    { key: "action", label: "", render: (r) => (
      <button onClick={(e) => { e.stopPropagation(); resend(r); }} data-testid={`resend-${r.id}`}
        className="inline-flex items-center gap-1.5 rounded-lg border border-white/12 px-2.5 py-1 text-xs text-slate-300 cc-nav-item">
        <Send size={12} /> Resend
      </button>
    )},
  ];

  return (
    <div data-testid="admin-emails-page">
      <APageHead title="Email Delivery" subtitle="Transactional email history, status tracking, and resend."
        action={<button onClick={load} className="inline-flex items-center gap-2 rounded-xl border border-white/12 px-3 py-2 text-sm text-slate-300 cc-nav-item" data-testid="emails-refresh"><RefreshCw size={15} /> Refresh</button>} />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <AMetric label="Sent" value={s.sent ?? 0} tone="blue" />
        <AMetric label="Delivered" value={s.delivered ?? 0} tone="green" />
        <AMetric label="Queued" value={s.queued ?? 0} tone="amber" />
        <AMetric label="Failed" value={s.failed ?? 0} tone="red" />
        <AMetric label="Bounced" value={s.bounced ?? 0} tone="red" />
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search recipient…"
          className="cc-input rounded-xl px-3 py-2 text-sm flex-1" data-testid="emails-search" />
        <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className="cc-input rounded-xl px-3 py-2 text-sm" data-testid="emails-status-filter">
          {["", "queued", "sent", "delivered", "failed", "bounced"].map((v) => <option key={v} value={v}>{v ? v[0].toUpperCase() + v.slice(1) : "All statuses"}</option>)}
        </select>
        <select value={category} onChange={(e) => { setPage(1); setCategory(e.target.value); }} className="cc-input rounded-xl px-3 py-2 text-sm" data-testid="emails-category-filter">
          {CATEGORIES.map((v) => <option key={v} value={v}>{v ? v.replace(/_/g, " ") : "All types"}</option>)}
        </select>
      </div>

      <ATable columns={columns} rows={data.items} loading={loading} empty="No emails sent yet." />
      <APager page={page} pages={data.pages} onPage={setPage} />
    </div>
  );
}
